import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { SeasonService } from 'src/app/services/season.service';
import { ApiBusinessService } from 'src/app/services/api-business.service';
import { InfoCardComponent } from '../../../shared/info-card/info-card.component';

@Component({
  selector: 'app-seasons-list',
  templateUrl: './seasons-list.component.html',
  styleUrls: ['./seasons-list.component.scss']
})
export class SeasonsListComponent implements OnInit {

  business: any;
  profile: any;
  seasons: any[] = [];
  loaded: boolean = false;
  loadingPage: boolean = true;

  activeFilter: string = '';
  searchText: string = '';

  statusDropdownOpen: boolean = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-group')) {
      this.statusDropdownOpen = false;
    }
  }

  private searchTerms = new Subject<string>();

  displayedColumns: string[] = ['actions', 'name', 'dates', 'status'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  @ViewChild(MatSort) sort: MatSort = new MatSort();

  constructor(
    private seasonService: SeasonService,
    private auth: AuthenticationService,
    private apiBusiness: ApiBusinessService,
    private dialog: MatDialog,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.profile = this.auth.getProfile();
  }

  ngAfterViewInit() {
    this.apiBusiness.changed.subscribe({
      next: (value) => {
        this.business = JSON.parse(localStorage.getItem('business') || '{}');
        if (value) {
          this.loadSeasons();
        }
      }
    });

    this.searchTerms.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchText = term;
      this.loadSeasons();
    });
  }

  onSearch(value: string) {
    this.searchTerms.next(value);
  }

  loadSeasons() {
    this.loadingPage = true;
    const companyId = this.business?.id;

    if (!companyId) {
      this.seasons = [];
      this.dataSource = new MatTableDataSource<any>([]);
      this.loaded = true;
      this.loadingPage = false;
      return;
    }

    this.seasonService.getSeasonsByCompany(companyId, this.searchText || undefined).subscribe({
      next: (res) => {
        let data = res.data || [];
        if (this.activeFilter !== '') {
          const isActive = this.activeFilter === 'true';
          data = data.filter((s: any) => s.active === isActive);
        }
        this.seasons = data;
        this.dataSource = new MatTableDataSource(this.seasons);
        this.dataSource.sort = this.sort;
        this.loaded = true;
        this.loadingPage = false;
      },
      error: () => {
        this.seasons = [];
        this.dataSource = new MatTableDataSource<any>([]);
        this.loaded = true;
        this.loadingPage = false;
      }
    });
  }

  onFilterChange() {
    this.loadSeasons();
  }

  clearFilters() {
    this.activeFilter = '';
    this.searchText = '';
    this.loadSeasons();
  }

  getDatesDisplay(season: any): string {
    const start = season.start_date ? new Date(season.start_date).toLocaleDateString('es-ES') : '...';
    const end = season.end_date ? new Date(season.end_date).toLocaleDateString('es-ES') : '...';
    return `${start} - ${end}`;
  }

  openCreate() {
    this.router.navigate(['/panel-control/sports-passes/seasons/create']);
  }

  openEdit(season: any) {
    this.router.navigate(['/panel-control/sports-passes/seasons/edit', season.id]);
  }

  openPasses(season: any) {
    this.router.navigate(['/panel-control/sports-passes/passes'], { queryParams: { id_season: season.id } });
  }

  toggleActive(season: any) {
    const action = season.active ? 'desactivar' : 'activar';
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { icon: true, text: `¿Estás seguro de que quieres ${action} esta temporada?`, btnCancel: true }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.seasonService.toggleSeasonActive(this.business.id, season.id).subscribe({
          next: () => {
            this.openInfoCard(`Temporada ${action === 'activar' ? 'activada' : 'desactivada'} correctamente`);
            this.loadSeasons();
          },
          error: (err) => {
            this.openInfoCard(this.extractErrorMessage(err, 'Error al cambiar el estado de la temporada'));
          }
        });
      }
    });
  }

  deleteSeason(season: any) {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      disableClose: true,
      data: { icon: true, text: '¿Estás seguro de que quieres eliminar esta temporada? Esta acción no podrá deshacerse.', btnCancel: true, warning: true }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.seasonService.deleteSeason(this.business.id, season.id).subscribe({
          next: () => {
            this.openInfoCard('Temporada eliminada correctamente');
            this.loadSeasons();
          },
          error: (err) => {
            this.openInfoCard(this.extractErrorMessage(err, 'Error al eliminar la temporada'));
          }
        });
      }
    });
  }

  private extractErrorMessage(err: any, fallback: string): string {
    if (err?.status === 422 && err?.error?.errors && typeof err.error.errors === 'object' && !Array.isArray(err.error.errors)) {
      const first: any = Object.values(err.error.errors)[0];
      if (Array.isArray(first) && first.length) {
        return first[0];
      }
    }
    return err?.error?.message || err?.error?.errors || fallback;
  }

  private openInfoCard(msg: string) {
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { text: msg }
    });
  }
}
